import { StoreOptions } from 'vuex';
import { actions } from '@/store/actions';
import { CatalogItem, Profile, ProfileGenderEnum, DealStageEnum, Deal } from '@/requests/mainApi';
import { Route } from 'vue-router';
import { store as UiStore } from '@/store/modules/ui';
import { AmoNotification } from '@/requests/mainApi/models/AmoNotification';
import { PassportData } from '@/requests/mainApi/models/PassportData';
import { Lead } from '@/requests/mainApi/models/Lead';
import { IInstruction } from '@/requests/mainApi/models/IInstruction';

export interface RootState {
  accessToken: string | null;
  temporaryAccessToken: string | null;
  isFromConfirmEmail: boolean;
  refreshToken: string | null;
  exp: number;
  userData: Profile;
  passportData: PassportData;
  favouriteFlats: CatalogItem[];
  notifications: AmoNotification[];
  notificationsCount: number;
  routeFrom: Route | null;
  deal: Deal;
  PPDAndTPDLinks: { [k: string]: string };
  organization: object;
  serviceModalOpen: boolean;
  PPDAndTPDConfirmModalOpen: boolean;
  flatDocumentsModalOpen: boolean;
  reminderModalOpen: boolean;
  approvalModalOpen: boolean;
  submitDocumentsModalOpen: boolean;
  isApartmentAcceptanceModalOpened: boolean;
  currentMortgageLink: string | null;
  currentReminderLink: string | null;
  currentLeadToApprove: Lead | null;
  currentLeadForDocuments: Lead | null;
  currentObjectDocs:
    | {
        uuid: string;
        name: string;
        date: string;
      }[]
    | null;
  apartmentAcceptanceFlatModel: null | CatalogItem;
  isMainActionModalOpened: boolean;
  instructions: IInstruction[]
}

const state: RootState = {
  temporaryAccessToken: null,
  isFromConfirmEmail: false,
  accessToken: null,
  refreshToken: null,
  exp: 0,
  userData: {
    firstName: '',
    lastName: '',
    patronymicName: '',
    email: '',
    birthday: '',
    isConfirmedEmail: false,
    favourites: [],
    docsIsUploaded: false,
    isVerified: false,
  },
  passportData: {
    passportAddress: '',
    passportBirthPlace: '',
    passportProvider: '',
    passportProviderCode: '',
    passportProviderDate: '',
    passportSeries: '',
    passportNumber: '',
  },
  notifications: [],
  notificationsCount: 0,
  favouriteFlats: [],
  routeFrom: null,
  deal: {},
  PPDAndTPDLinks: {
    ppd: '',
    tpd: '',
  },
  serviceModalOpen: false,
  PPDAndTPDConfirmModalOpen: false,
  approvalModalOpen: false,
  submitDocumentsModalOpen: false,
  reminderModalOpen: false,
  flatDocumentsModalOpen: false,
  isApartmentAcceptanceModalOpened: false,
  currentMortgageLink: null,
  currentReminderLink: null,
  currentObjectDocs: null,
  currentLeadToApprove: null,
  currentLeadForDocuments: null,
  apartmentAcceptanceFlatModel: null,
  isMainActionModalOpened: false,
  instructions: [],
  organization: []
};

export const store: StoreOptions<RootState> = {
  state,
  getters: {
    notifications(state) {
      return state.notifications;
    },
    notificationsCount(state) {
      return state.notificationsCount;
    },
    unreadNotifications(state) {
      return state.notifications.filter(({ isRead }) => isRead === 0);
    },
    temporaryAccessToken(state) {
      return state.temporaryAccessToken;
    },
    isFromConfirmEmail(state) {
      return state.isFromConfirmEmail;
    },
    accessToken(state) {
      return state.accessToken;
    },
    refreshToken(state) {
      return state.refreshToken;
    },
    userData(state) {
      return state.userData;
    },
    favouriteFlats(state) {
      return state.favouriteFlats;
    },
    profileIsCompleted(state): boolean {
      return Boolean(
        state.userData.gender !== ProfileGenderEnum['Unknown'] &&
          state.userData.gender &&
          state.userData.firstName &&
          state.userData.lastName &&
          state.userData.birthday
      );
    },
    routeFrom(state) {
      return state.routeFrom;
    },
    deal(state) {
      return state.deal;
    },
    noActionStages() {
      return ['send_deal_data', 'check_contract_signed', 'transaction', 'pay_under_contract', 'final'];
    },
    isNoActionStage: (state, getters) => (stage: DealStageEnum | undefined) => {
      return getters.noActionStages.includes(stage || state.deal.stage!);
    },
    PPDAndTPDLinks(state) {
      return state.PPDAndTPDLinks;
    },
    organization(state) {
      return state.organization;
    },
    serviceModalOpen(state) {
      return state.serviceModalOpen;
    },
    PPDAndTPDConfirmModalOpen(state) {
      return state.PPDAndTPDConfirmModalOpen;
    },
    reminderModalOpen(state) {
      return state.reminderModalOpen;
    },
    approvalModalOpen(state) {
      return state.approvalModalOpen;
    },
    flatDocumentsModalOpen(state) {
      return state.flatDocumentsModalOpen;
    },
    isApartmentAcceptanceModalOpened(state) {
      return state.isApartmentAcceptanceModalOpened;
    },
    currentMortgageLink(state) {
      return state.currentMortgageLink;
    },
    currentReminderLink(state) {
      return state.currentReminderLink;
    },
    currentObjectDocs(state) {
      return state.currentObjectDocs;
    },
    passportData(state) {
      return state.passportData;
    },
    currentLeadToApprove(state) {
      return state.currentLeadToApprove;
    },
    submitDocumentsModalOpen(state) {
      return state.submitDocumentsModalOpen;
    },
    currentLeadForDocuments(state) {
      return state.currentLeadForDocuments;
    },
    apartmentAcceptanceFlatModel(state) {
      return state.apartmentAcceptanceFlatModel;
    },
    isMainActionModalOpened(state) {
      return state.isMainActionModalOpened;
    },
    instructions(state) {
      return state.instructions;
    },
  },
  mutations: {
    setTemporaryAccessToken(state, value) {
      state.temporaryAccessToken = `${value}`;
    },
    setIsFromConfirmEmail(state, value) {
      state.isFromConfirmEmail = value;
    },
    setAccessToken(state, value) {
      state.accessToken = `${value}`;
      localStorage.setItem('accessToken', `${state.accessToken}`);
    },
    setRefreshToken(state, value) {
      state.refreshToken = `${value}`;
      localStorage.setItem('refreshToken', `${state.refreshToken}`);
    },
    setUserData(state, value) {
      state.userData = { ...state.userData, ...value };
    },
    setFavouriteFlats(state, value) {
      state.favouriteFlats = value;
    },
    setNotifications(state, value) {
      state.notifications = value;
    },
    setNotificationsCount(state, value) {
      state.notificationsCount = value;
    },
    setRouteFrom(state, value) {
      state.routeFrom = value;
    },
    setDeal(state, value) {
      state.deal = value;
    },
    setPPDAndTPDLinks(state, value) {
      state.PPDAndTPDLinks = value;
    },
    setOrganization(state, value) {
      state.organization = value;
    },
    setServiceModalOpen(state, value) {
      state.serviceModalOpen = value;
    },
    setPPDAndTPDConfirmModalOpen(state, value) {
      state.PPDAndTPDConfirmModalOpen = value;
    },
    setReminderModalOpen(state, value) {
      state.reminderModalOpen = value;
    },
    setApprovalModalOpen(state, value) {
      state.approvalModalOpen = value;
    },
    setFlatDocumentsModalOpen(state, value) {
      state.flatDocumentsModalOpen = value;
    },
    setApartmentAcceptanceModalOpened(state, value) {
      state.isApartmentAcceptanceModalOpened = value;
    },
    setApartmentAcceptanceFlatModel(state, value) {
      state.apartmentAcceptanceFlatModel = value;
    },
    setCurrentMortgageLink(state, value) {
      state.currentMortgageLink = value;
    },
    setCurrentReminderLink(state, value) {
      state.currentReminderLink = value;
    },
    setCurrentObjectDocs(state, value) {
      state.currentObjectDocs = value;
    },
    setPassportData(state, value) {
      state.passportData = value;
    },
    setCurrentLeadToApprove(state, value) {
      state.currentLeadToApprove = value;
    },
    setSubmitDocumentsModalOpen(state, value) {
      state.submitDocumentsModalOpen = value;
    },
    setCurrentLeadForDocuments(state, value) {
      state.currentLeadForDocuments = value;
    },
    setMainActionModalOpened(state, value) {
      state.isMainActionModalOpened = value;
    },
    setInstructions(state, value) {
      state.instructions = value;
    },
  },
  actions,
  modules: {
    ui: UiStore,
  },
};
